import {
    Box, FormControl, Grid, Card, CardContent, Typography, Chip, Avatar,
    LinearProgress, Tooltip,
    CircularProgress
} from "@mui/material";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import CommonDatePicker from "../../../Base/views/CommonDatePicker";
import CustomToggle from "../../../Base/views/CustomToggle";
import MaterialTable from "material-table";
import { useState, useEffect, useMemo } from "react";
import { GetTargetAchievementAction, GetReportRecruiterAction } from "../../../../../store/ATS/Report/Action";
import { useDispatch, useSelector } from "react-redux";
import Chart from "react-apexcharts";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";

// ─── Brand ─────────────────────────────────────────────────────────────────────
const BRAND = "#8B44FF";
const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

const fyStart = () => {
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${y}-04-01`;
};
const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${("0" + (d.getMonth() + 1)).slice(-2)}-${("0" + d.getDate()).slice(-2)}`;
};
const pctColor = (p) => p >= 75 ? "#10B981" : p >= 50 ? "#F59E0B" : "#EF4444";

// ─── KPI Card ──────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, value, label, sub, color }) => (
    <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: `${color}18`, color, width: 42, height: 42 }}>{icon}</Avatar>
                <Box>
                    <Typography fontWeight={800} fontSize="1.35rem" lineHeight={1}>{value}</Typography>
                    <Typography fontSize="0.75rem" color="text.secondary">{label}</Typography>
                    {sub && <Typography fontSize="0.68rem" color={color} fontWeight={600}>{sub}</Typography>}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Main Component ─────────────────────────────────────────────────────────────
const RecruiterAchievementReportNew = () => {
    const dispatch = useDispatch();
    const [localIsDashboardView, setLocalIsDashboardView] = useState(true);
    const [userData, setUserData] = useState({ load: true, values: [] });
    const [filters, setFilters] = useState({
        from_date: fyStart(),
        to_date: todayStr(),
        primary_recruiter_filter: "",
    });

    const { atsTargetAchievement, isTargetRepLoading } = useSelector(s => s.AtsReducer?.AtsReportReducer);
    const { atsRecruiterData } = useSelector(s => s.AtsReducer?.AtsReportReducer);

    // ── Bootstrap recruiter dropdown ──────────────────────────────────────────
    useEffect(() => {
        dispatch(GetReportRecruiterAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (atsRecruiterData?.length > 0) setUserData({ load: false, values: atsRecruiterData });
    }, [atsRecruiterData]);

    // ── Fetch achievement data ────────────────────────────────────────────────
    useEffect(() => {
        dispatch(GetTargetAchievementAction({
            from_date: filters.from_date,
            to_date: filters.to_date,
            primary_recruiter: filters.primary_recruiter_filter
                ? `{${filters.primary_recruiter_filter.ats_users_id}}`
                : null,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.from_date, filters.to_date, filters.primary_recruiter_filter]);

    const handleFilter = (event, value) => {
        if (event === "primary_recruiter_filter") setFilters(f => ({ ...f, primary_recruiter_filter: value || "" }));
        if (["from_date", "to_date"].includes(event?.target?.name)) setFilters(f => ({ ...f, [event.target.name]: event.target.value }));
    };

    // ── Aggregate per recruiter ───────────────────────────────────────────────
    const activity = atsTargetAchievement || [];

    const recruiterMap = useMemo(() => {
        const map = {};
        activity?.forEach(r => {
            const name = r.recruiter_name || "Unknown";
            if (!map[name]) map[name] = { proposed: 0, interviewed: 0, selected: 0, rejected: 0, clients: new Set(), jds: new Set() };
            map[name].proposed += parseInt(r.shortlisted) || 0;
            map[name].interviewed += parseInt(r.interviews_scheduled) || 0;
            map[name].selected += parseInt(r.selected) || 0;
            map[name].rejected += parseInt(r.rejected) || 0;
            if (r.company_name) map[name].clients.add(r.company_name);
            if (r.job_title) map[name].jds.add(r.job_title);
        });
        return map;
    }, [activity]);

    const recruiterList = Object.entries(recruiterMap)
        ?.map(([name, v]) => ({
            name,
            proposed: v.proposed,
            interviewed: v.interviewed,
            selected: v.selected,
            rejected: v.rejected,
            clients: v.clients.size,
            jds: v.jds.size,
            selectionRate: v.proposed > 0 ? Math.round((v.selected / v.proposed) * 100) : 0,
            interviewRate: v.proposed > 0 ? Math.round((v.interviewed / v.proposed) * 100) : 0,
        }))
        ?.sort((a, b) => b.selected - a.selected);

    // KPIs
    const totalRecruiters = recruiterList?.length || 0;
    const totalProposed = recruiterList?.reduce((s, r) => s + r.proposed, 0) || 0;
    const totalSelected = recruiterList?.reduce((s, r) => s + r.selected, 0) || 0;
    const totalInterviewed = recruiterList?.reduce((s, r) => s + r.interviewed, 0) || 0;
    const avgSelectionRate = totalRecruiters > 0
        ? Math.round(recruiterList?.reduce((s, r) => s + r.selectionRate, 0) / totalRecruiters)
        : 0;
    const topRecruiter = recruiterList[0];

    // ── Charts ────────────────────────────────────────────────────────────────
    const top10 = recruiterList.slice(0, 10);

    // Grouped bar: Proposed vs Interviewed vs Selected per recruiter
    const activityBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: [BRAND, "#4E79A7", "#10B981"],
            plotOptions: { bar: { columnWidth: "60%", borderRadius: 4 } },
            dataLabels: { enabled: false },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            xaxis: { categories: top10.map(r => r.name.split(" ")[0]), labels: { style: { fontSize: "11px", fontWeight: 600 }, rotate: -30 } },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            legend: { position: "top", fontSize: "12px", markers: { radius: 3 } },
        },
        series: [
            { name: "Proposed", data: top10.map(r => r.proposed) },
            { name: "Interviewed", data: top10.map(r => r.interviewed) },
            { name: "Selected", data: top10.map(r => r.selected) },
        ],
    };

    // Radial bar: Top 3 selection rates
    const top3 = recruiterList.slice(0, 3);
    const radialChart = {
        options: {
            chart: { type: "radialBar", fontFamily: "Nunito Sans" },
            colors: [BRAND, "#4E79A7", "#10B981"],
            plotOptions: {
                radialBar: {
                    hollow: { size: "35%" },
                    track: { background: "#F1F5F9", strokeWidth: "100%" },
                    dataLabels: {
                        name: { fontSize: "11px", fontWeight: 600, offsetY: -5 },
                        value: { fontSize: "14px", fontWeight: 800, formatter: v => `${Math.round(v)}%` },
                    },
                },
            },
            labels: top3.map(r => r.name.split(" ")[0]),
            stroke: { lineCap: "round" },
            legend: { show: false },
        },
        series: top3.map(r => Math.min(r.selectionRate, 100)),
    };

    // Horizontal bar: Selection rate ranking
    const selRateBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: recruiterList.slice(0, 12).map(r => pctColor(r.selectionRate)),
            plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true, barHeight: "60%" } },
            dataLabels: { enabled: true, formatter: v => `${v}%`, style: { fontSize: "11px", fontWeight: 600 } },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
            xaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } }, max: 100, title: { text: "Selection Rate (%)", style: { fontSize: "11px" } } },
            yaxis: { labels: { style: { fontSize: "11px", fontWeight: 500 } } },
            legend: { show: false },
            annotations: avgSelectionRate > 0 ? {
                xaxis: [{ x: avgSelectionRate, borderColor: "#F59E0B", strokeDashArray: 4, label: { text: `Avg: ${avgSelectionRate}%`, style: { fontSize: "10px", color: "#D97706", background: "#FEF3C7" } } }]
            } : {},
        },
        series: [{ data: recruiterList.slice(0, 12).map(r => ({ x: r.name.split(" ")[0], y: r.selectionRate })) }],
    };

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns = [
        {
            title: "#", field: "rank", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center", width: 50 },
            headerStyle: { padding: "10px", textAlign: "center", width: 50 },
            render: (_, i) => <Typography fontWeight={700} fontSize="0.82rem" color="#94A3B8">{i + 1}</Typography>,
        },
        {
            title: "Recruiter", field: "name",
            cellStyle: { padding: "10px", fontWeight: 600 }, headerStyle: { padding: "10px" },
        },
        {
            title: "Proposed", field: "proposed", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => <Chip label={r.proposed} size="small" sx={{ bgcolor: "#EDE9FE", color: BRAND, fontWeight: 700 }} />,
        },
        {
            title: "Interviewed", field: "interviewed", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => <Chip label={r.interviewed} size="small" sx={{ bgcolor: "#DBEAFE", color: "#2563EB", fontWeight: 700 }} />,
        },
        {
            title: "Selected", field: "selected", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => <Chip label={r.selected} size="small" sx={{ bgcolor: "#D1FAE5", color: "#059669", fontWeight: 700 }} />,
        },
        {
            title: "Rejected", field: "rejected", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => <Chip label={r.rejected} size="small" sx={{ bgcolor: "#FEE2E2", color: "#DC2626", fontWeight: 700 }} />,
        },
        {
            title: "Clients", field: "clients", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
        },
        {
            title: "Interview Rate", field: "interviewRate", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
                    <Typography fontWeight={700} fontSize="0.8rem" color={pctColor(r.interviewRate)}>{r.interviewRate}%</Typography>
                    <LinearProgress variant="determinate" value={Math.min(r.interviewRate, 100)}
                        sx={{ width: "60px", height: 4, borderRadius: 2, bgcolor: "#F1F5F9", "& .MuiLinearProgress-bar": { bgcolor: "#4E79A7", borderRadius: 2 } }}
                    />
                </Box>
            ),
        },
        {
            title: "Selection Rate", field: "selectionRate", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
                    <Typography fontWeight={700} fontSize="0.8rem" color={pctColor(r.selectionRate)}>{r.selectionRate}%</Typography>
                    <LinearProgress variant="determinate" value={Math.min(r.selectionRate, 100)}
                        sx={{ width: "60px", height: 4, borderRadius: 2, bgcolor: "#F1F5F9", "& .MuiLinearProgress-bar": { bgcolor: pctColor(r.selectionRate), borderRadius: 2 } }}
                    />
                </Box>
            ),
        },
    ];

    return (
        <>
            <Box sx={{ mt: 1 }}>
                {/* ── Filters & View Toggle ─────────────────────────────── */}
                <Grid container columnSpacing={1} rowSpacing={1} mb={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <SelectableSearch
                            isOptionEqualToValue={(o, v) => o?.ats_users_id === v?.ats_users_id}
                            label="Recruiter" value={filters.primary_recruiter_filter}
                            loading={() => setUserData({ load: false, values: atsRecruiterData })}
                            fieldLabel="first_name" required={false}
                            variant="outlined" name="primary_recruiter_filter"
                            onChangeEvent={handleFilter} data={userData} size="small"
                        />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.5}>
                        <FormControl fullWidth>
                            <CommonDatePicker
                                size="small" name="from_date" label="From Date" pickerType="date"
                                onKeyDown={e => e.preventDefault()}
                                pickerFormat="dd/MM/yyyy" DefaultDTvalue={filters.from_date} maxDate={new Date()}
                                handleChange={v => handleFilter({ target: { name: "from_date", value: v ? `${v.getFullYear()}-${("0" + (v.getMonth() + 1)).slice(-2)}-${("0" + v.getDate()).slice(-2)}` : "" } })}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.5}>
                        <FormControl fullWidth>
                            <CommonDatePicker
                                size="small" name="to_date" label="To Date" pickerType="date"
                                onKeyDown={e => e.preventDefault()}
                                pickerFormat="dd/MM/yyyy" DefaultDTvalue={filters.to_date} maxDate={new Date()} minDate={filters.from_date || null}
                                handleChange={v => handleFilter({ target: { name: "to_date", value: v ? `${v.getFullYear()}-${("0" + (v.getMonth() + 1)).slice(-2)}-${("0" + v.getDate()).slice(-2)}` : "" } })}
                            />
                        </FormControl>
                    </Grid>
                    {isTargetRepLoading && (
                        <Grid item xs="auto" sx={{ display: "flex", alignItems: "center" }}>
                            <CircularProgress size={22} disableShrink/>
                        </Grid>
                    )}
                    <Grid item xs sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, alignItems: "center" }}>
                        {setLocalIsDashboardView && (
                            <CustomToggle
                                isChecked={!localIsDashboardView}
                                handleToggleChange={() => setLocalIsDashboardView(!localIsDashboardView)}
                                Yestext={"Table"}
                                NoText={"Dashboard"}
                                YesTooltip={"Switch to Table View"}
                                NoTooltip={"Switch to Dashboard View"}
                                style={{ height: "37px", labelPadding: "2px 8px" }}
                            />
                        )}
                    </Grid>
                </Grid>

                {localIsDashboardView ? (
                    <>
                        {/* ── KPI Cards ─────────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<GroupsOutlinedIcon sx={{ fontSize: 20 }} />} value={totalRecruiters} label="Active Recruiters" color="#4E79A7" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<ShareOutlinedIcon sx={{ fontSize: 20 }} />} value={totalProposed} label="Total Proposed" sub={`${totalInterviewed} interviewed`} color={BRAND} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<CheckCircleOutlineIcon sx={{ fontSize: 20 }} />} value={totalSelected} label="Total Selected" sub={`${totalProposed > 0 ? Math.round(totalSelected / totalProposed * 100) : 0}% selection rate`} color="#10B981" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<TrendingUpIcon sx={{ fontSize: 20 }} />} value={`${avgSelectionRate}%`} label="Avg Selection Rate"
                                    sub={topRecruiter ? `Best: ${topRecruiter.name.split(" ")[0]} (${topRecruiter.selected})` : "—"}
                                    color={avgSelectionRate >= 75 ? "#10B981" : avgSelectionRate >= 50 ? "#F59E0B" : "#EF4444"}
                                />
                            </Grid>
                        </Grid>

                        {/* ── Charts Row 1 ───────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={7}>
                                <Card>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Activity by Recruiter (Top 10)</Typography>
                                        <Typography variant="caption" color="text.secondary">Proposed · Interviewed · Selected per recruiter</Typography>
                                        {top10.length > 0 ? (
                                            <Chart options={activityBar.options} series={activityBar.series} type="bar" height={270} />
                                        ) : (
                                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 240, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data for selected period</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Selection Rate Ranking</Typography>
                                        <Typography variant="caption" color="text.secondary">% of proposed candidates selected (avg line shown)</Typography>
                                        {recruiterList.length > 0 ? (
                                            <Chart options={selRateBar.options} series={selRateBar.series} type="bar" height={270} />
                                        ) : (
                                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 240, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* ── Charts Row 2: Radial + Recruiter cards ─────────────── */}
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} md={3}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                                            <EmojiEventsIcon sx={{ fontSize: 16, mr: 0.5, color: "#FECB03", verticalAlign: "text-bottom" }} />
                                            Top 3 Performers
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">Selection rate gauge</Typography>
                                        {top3.length > 0 ? (
                                            <Chart options={radialChart.options} series={radialChart.series} type="radialBar" height={250} />
                                        ) : (
                                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 220, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={9}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                            <Typography variant="subtitle2" fontWeight={700}>All Recruiters — Achievement Cards</Typography>
                                            <Chip label={`${totalRecruiters} recruiters`} size="small" sx={{ bgcolor: "#F3EDFD", color: BRAND, fontWeight: 600, fontSize: "0.68rem" }} />
                                        </Box>
                                        <Grid container spacing={1}>
                                            {recruiterList.map((r, i) => {
                                                const rate = r.selectionRate;
                                                const color = pctColor(rate);
                                                return (
                                                    <Grid item xs={12} sm={6} md={4} key={r.name}>
                                                        <Tooltip title={`${r.clients} clients · ${r.jds} active JDs`} placement="top">
                                                            <Card variant="outlined" sx={{ borderColor: "rgba(0,0,0,0.08)", transition: "all 0.2s", "&:hover": { borderColor: BRAND, boxShadow: `0 4px 14px ${BRAND}20`, transform: "translateY(-2px)" } }}>
                                                                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1 }}>
                                                                        <Avatar sx={{ width: 32, height: 32, bgcolor: i < 3 ? `${medalColors[i]}20` : "#F3EDFD", color: i < 3 ? medalColors[i] : BRAND, fontSize: "0.7rem", fontWeight: 800 }}>
                                                                            {i < 3 ? <EmojiEventsIcon sx={{ fontSize: 16 }} /> : `#${i + 1}`}
                                                                        </Avatar>
                                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                            <Typography variant="body2" fontWeight={650} fontSize="0.8rem" noWrap>{r.name}</Typography>
                                                                            <Typography variant="caption" color="text.secondary">{r.selected} selected</Typography>
                                                                        </Box>
                                                                        <Typography fontSize="0.88rem" fontWeight={800} color={color}>{rate}%</Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                                                                        <Typography variant="caption" color="text.secondary">Proposed: {r.proposed}</Typography>
                                                                        <Typography variant="caption" color="text.secondary">Interviewed: {r.interviewed}</Typography>
                                                                    </Box>
                                                                    <LinearProgress variant="determinate" value={Math.min(rate, 100)}
                                                                        sx={{ height: 5, borderRadius: 3, bgcolor: "#F1F5F9", "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 3 } }}
                                                                    />
                                                                </CardContent>
                                                            </Card>
                                                        </Tooltip>
                                                    </Grid>
                                                );
                                            })}
                                            {recruiterList.length === 0 && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 120, color: "#94A3B8" }}>
                                                        <Typography fontSize="0.85rem">No recruiter data for selected period</Typography>
                                                    </Box>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </>
                ) : (
                    /* ── Detail Table ──────────────────────────────────────── */
                    <Box sx={{ mt: 1 }}>
                        <MaterialTable
                            columns={columns}
                            data={recruiterList}
                            options={{
                                maxBodyHeight: "calc(100vh - 280px)",
                                minBodyHeight: "350px",
                                sorting: true,
                                search: true,
                                searchFieldAlignment: "right",
                                searchAutoFocus: true,
                                searchFieldVariant: "standard",
                                filtering: false,
                                exportButton: true,
                                exportAllData: true,
                                paging: true,
                                pageSizeOptions: [25, 50, 100],
                                pageSize: 25,
                                actionsColumnIndex: -1,
                                draggable: false,
                                columnsButton: true,
                                headerStyle: {
                                    backgroundColor: "#fff",
                                    color: "#64748B",
                                    fontWeight: 700,
                                    fontSize: "0.78rem",
                                    whiteSpace: "nowrap",
                                    padding: "10px",
                                },
                            }}
                            title={`Recruiter Achievement (${totalRecruiters} recruiters · ${filters.from_date} → ${filters.to_date})`}
                        />
                    </Box>
                )}
            </Box>
        </>
    );
};

export default RecruiterAchievementReportNew;
